import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";
import { useTr } from "@/i18n/translator";
import PasswordInput from "@/components/PasswordInput";

export default function Login() {
  const tr = useTr();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const r = await login(email, password, needTotp ? totp : undefined);
    setLoading(false);
    if (r.ok) { nav("/dashboard"); return; }
    if (r.twofa) { setNeedTotp(true); setErr(""); return; }
    setErr(r.error);
  };

  return (
    <AuthLayout title={tr("Acceso autorizado")} subtitle={tr("INICIA SESIÓN EN TU CUENTA")}>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <Field label="EMAIL">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]"
            placeholder={tr("tu@empresa.com")}
            data-testid="login-email"
          />
        </Field>
        <Field label="CONTRASEÑA">
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            testid="login-password"
          />
        </Field>
        {needTotp && (
          <Field label="CÓDIGO 2FA · 6 DÍGITOS">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              required
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
              className="w-full bg-white border border-[#E53935] px-4 py-3 font-mono-data text-lg tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-[#E53935]"
              placeholder="000000"
              data-testid="login-totp"
              autoFocus
            />
            <div className="mt-2 font-mono-mark text-[9px] text-[#5C5C5C]">
              {tr("INGRESA EL CÓDIGO DE TU APP DE AUTENTICACIÓN")}
            </div>
          </Field>
        )}
        {err && (
          <div className="border border-[#E53935] bg-[#E53935]/10 px-4 py-3 font-mono-data text-sm text-[#E53935]" data-testid="login-error">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#0A0A0A] text-white px-4 py-3 font-mono-mark text-xs hover-brutal border border-[#0A0A0A] flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="login-submit"
        >
          {loading ? tr("VERIFICANDO…") : tr("ENTRAR")}
          {!loading && <ArrowRight className="w-4 h-4" />}
        </button>
        <div className="text-center font-mono-data text-sm">
          {tr("¿Sin cuenta?")}{" "}
          <Link to="/register" className="underline decoration-[#E53935] underline-offset-4" data-testid="login-go-register">
            {tr("Crea una")}
          </Link>
        </div>
        <div className="text-center font-mono-data text-sm -mt-2">
          <Link to="/forgot-password" className="text-[#5C5C5C] hover:text-[#E53935] underline decoration-[#E53935] underline-offset-4" data-testid="login-forgot">
            {tr("¿Olvidaste tu contraseña?")}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export function AuthLayout({ title, subtitle, children }) {
  const tr = useTr();
  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0A0A0A] flex">
      <div className="hidden md:flex w-1/2 bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="dossier-grid absolute inset-0 opacity-10" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-3" data-testid="auth-logo">
            <img src="/logo-cia.png" alt="CIA Marketing" className="w-10 h-10 object-contain" />
            <span className="font-display text-lg">CIA MARKETING<span className="text-[#E53935]">®</span></span>
          </Link>
          <div>
            <div className="font-mono-mark text-[11px] text-[#FFD600]">CLASIFICADO · NIVEL 1</div>
            <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-none">
              {tr("Tu marca,")}<br />en <span className="bg-[#E53935] px-2">autopilot</span>.
            </h1>
            <p className="mt-6 font-mono-data text-sm text-white/70 max-w-md">
              {tr("Genera, programa y publica en todas tus redes sociales con IA. Sin contratar agencia.")}
            </p>
          </div>
          <div className="font-mono-mark text-[10px] text-white/50">DOSSIER · 2026 · GLOBAL</div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="font-mono-mark text-[11px] text-[#5C5C5C]">{subtitle}</div>
          <h2 className="font-display text-4xl mt-3 mb-8">{title}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
